/**
 * Coordinate transformation utilities
 * Converts between lat/lng coordinates and meters
 *
 * Extracted from GeoTiffViewerEmbedded.tsx to eliminate duplication
 * and provide consistent coordinate handling across the application.
 */

import { LatLng, Point2D } from './types';

/**
 * Approximate meters per degree of latitude (constant globally)
 * At the equator: 1 degree latitude ≈ 111,320 meters
 */
export const METERS_PER_DEGREE_LAT = 111320;

/**
 * Calculate meters per degree of longitude at a given latitude
 * Longitude degrees get smaller as you move toward the poles
 *
 * @param lat - Latitude in degrees
 * @returns Meters per degree of longitude at that latitude
 */
export function getMetersPerDegreeLng(lat: number): number {
  return METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);
}

/**
 * Convert lat/lng coordinates to meters relative to an origin point
 * Useful for calculations that need metric distances
 *
 * @param latLng - Point to convert
 * @param origin - Reference origin point
 * @returns Point in meters relative to origin
 */
export function latLngToMeters(latLng: LatLng, origin: LatLng): Point2D {
  const dx = (latLng.lng - origin.lng) * getMetersPerDegreeLng(origin.lat);
  const dy = (latLng.lat - origin.lat) * METERS_PER_DEGREE_LAT;
  return { x: dx, y: dy };
}

/**
 * Convert meters back to lat/lng coordinates relative to an origin
 * Inverse of latLngToMeters
 *
 * @param meters - Point in meters
 * @param origin - Reference origin point
 * @returns Lat/lng coordinates
 */
export function metersToLatLng(meters: Point2D, origin: LatLng): LatLng {
  return {
    lat: origin.lat + meters.y / METERS_PER_DEGREE_LAT,
    lng: origin.lng + meters.x / getMetersPerDegreeLng(origin.lat),
  };
}

/**
 * Calculate straight-line distance between two points in meters
 * Uses Euclidean distance in projected meter space
 *
 * @param p1 - First point
 * @param p2 - Second point
 * @returns Distance in meters
 */
export function calculateDistance(p1: LatLng, p2: LatLng): number {
  const meters = latLngToMeters(p2, p1);
  return Math.sqrt(meters.x ** 2 + meters.y ** 2);
}

/**
 * Calculate bearing (compass direction) from one point to another
 * Returns angle in degrees (0° = North, 90° = East, etc.)
 *
 * @param from - Starting point
 * @param to - Destination point
 * @returns Bearing in degrees (0-360)
 */
export function calculateBearing(from: LatLng, to: LatLng): number {
  const meters = latLngToMeters(to, from);
  const bearing = (Math.atan2(meters.x, meters.y) * 180) / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360
}

/**
 * Move a point by a given distance and bearing
 *
 * @param origin - Starting point
 * @param distanceMeters - Distance to move (meters)
 * @param bearingDegrees - Direction to move (0° = North)
 * @returns New point after movement
 */
export function movePoint(
  origin: LatLng,
  distanceMeters: number,
  bearingDegrees: number
): LatLng {
  const bearingRad = (bearingDegrees * Math.PI) / 180;
  const dx = distanceMeters * Math.sin(bearingRad);
  const dy = distanceMeters * Math.cos(bearingRad);

  return metersToLatLng({ x: dx, y: dy }, origin);
}

/**
 * Get degrees per meter conversion factors at a given latitude
 * Useful for quick lat/lng offset calculations
 *
 * @param lat - Latitude in degrees
 * @returns Object with lat and lng conversion factors
 */
export function getDegreesPerMeter(lat: number) {
  return {
    lat: 1 / METERS_PER_DEGREE_LAT,
    lng: 1 / getMetersPerDegreeLng(lat),
  };
}

/**
 * Create a small offset for duplication or spacing
 *
 * @param point - Original point
 * @param offsetMeters - Offset distance in meters
 * @param lat - Latitude for accurate conversion
 * @returns New point with offset applied
 */
export function offsetPoint(
  point: [number, number],
  offsetMeters: number,
  lat: number
): [number, number] {
  const latDegPerMeter = 1 / METERS_PER_DEGREE_LAT;
  const lngDegPerMeter = 1 / getMetersPerDegreeLng(lat);

  return [
    point[0] + offsetMeters * latDegPerMeter,
    point[1] + offsetMeters * lngDegPerMeter,
  ];
}
